import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

/** Human-readable duration from milliseconds: "—", "46ms", "1.2s", "3m 4s". */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}m ${seconds}s`;
}

export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = (Date.now() - d.getTime()) / 1000;
  // Clock skew between Postgres and the browser can produce a tiny negative
  // diff for very recent inserts; treat any future or sub-second past
  // timestamp as "just now" rather than rendering "-3s ago".
  if (diff < 60) return diff < 1 ? 'just now' : `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/** Absolute local timestamp for hover titles; "never" when null/undefined. */
export function absoluteStamp(date: string | Date | null | undefined): string {
  if (!date) return 'never';
  return (typeof date === 'string' ? new Date(date) : date).toLocaleString();
}

export type Freshness = 'fresh' | 'recent' | 'stale';

/** Bucket a last-collected timestamp: fresh <1h, recent <24h, stale >=24h (or never). */
export function freshnessOf(lastCollectedAt: string | Date | null | undefined): Freshness {
  if (!lastCollectedAt) return 'stale';
  const ms = Date.now() - new Date(lastCollectedAt).getTime();
  if (Number.isNaN(ms)) return 'stale';
  if (ms < 3_600_000) return 'fresh';
  if (ms < 86_400_000) return 'recent';
  return 'stale';
}

/**
 * Providers we recognise from a Hangfire job class name. Anything else
 * (system jobs, RecurringJob trampolines, etc.) parses to null. Kept in sync
 * with the anchored prefix in `parseHarvestJob`'s regex.
 */
export type HarvestProvider = 'Genius' | 'Presto';

/**
 * Parse a Hangfire invocation into a provider + friendly harvest-job label.
 *
 * `invocationdata` is the `jsonb` column from `harvest.job`; PostgREST returns
 * it already-parsed (object), but older Hangfire stored it as text, so we also
 * tolerate a JSON string (matching `summarizeInvocation` in HangfireRoute).
 *
 * The `Type` is a fully-qualified class name like
 * `RevoDataPlatform.Collector.Jobs.GeniusLeaguesHarvestJob, RevoDataPlatform.Collector`.
 * We take the class name (last segment before the assembly comma), require it to
 * start with a known provider and end in `HarvestJob`/`SyncJob`, then turn the
 * middle CamelCase into a lower-case label:
 *   `GeniusLeaguesHarvestJob` -> { provider: 'Genius', label: 'leagues harvest' }
 *   `PrestoFixturesSyncJob`   -> { provider: 'Presto', label: 'fixtures sync' }
 * Returns null for anything that isn't a Genius/Presto harvest|sync job.
 */
export function parseHarvestJob(
  invocationdata: unknown,
): { provider: HarvestProvider; label: string } | null {
  if (invocationdata == null) return null;

  let parsed: { Type?: string } | null = null;
  if (typeof invocationdata === 'string') {
    try {
      parsed = JSON.parse(invocationdata) as { Type?: string };
    } catch {
      return null;
    }
  } else if (typeof invocationdata === 'object') {
    parsed = invocationdata as { Type?: string };
  } else {
    return null;
  }

  const type = parsed?.Type;
  if (!type) return null;

  // Strip the assembly suffix, then take the class name.
  const className = type.split(',')[0].split('.').pop() ?? '';
  if (!className) return null;

  // The anchored prefix enforces the provider; the middle segment is optional
  // so top-level jobs like `PrestoHarvestJob`/`PrestoSyncJob` (no entity) match.
  const match = /^(Genius|Presto)(.*?)(Harvest|Sync)Job$/.exec(className);
  if (!match) return null;

  const [, provider, middle, verb] = match;
  // Split the CamelCase entity portion ("LeaguesByCompetition" -> "leagues by competition").
  const words = middle
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase();

  const label = [words, verb.toLowerCase()].filter(Boolean).join(' ');
  return { provider: provider as HarvestProvider, label };
}
